<script setup lang="ts">
// The organization's active users and whether each already names a VectorSurv
// user to file under. Nothing is mapped yet, so every user reads "not mapped" —
// that is the gap this page exists to show, not a failure.
import { onMounted, ref } from "vue";

import { apiClient } from "@/client";
import type { VectorSurvConfig, VectorSurvUser } from "@/type/vectorsurv";

const loading = ref(true);
const error = ref("");
const tokenConfigured = ref(false);
const users = ref<VectorSurvUser[]>([]);
const usersLoading = ref(false);
const usersError = ref("");

async function loadUsers(): Promise<void> {
	usersLoading.value = true;
	usersError.value = "";
	try {
		users.value = await apiClient.JSONGet<VectorSurvUser[]>(
			"/api/integration/vectorsurv/users",
		);
	} catch (e: any) {
		usersError.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load organization users.";
	} finally {
		usersLoading.value = false;
	}
}

async function load(): Promise<void> {
	loading.value = true;
	error.value = "";
	try {
		const config = await apiClient.JSONGet<VectorSurvConfig>(
			"/api/integration/vectorsurv",
		);
		tokenConfigured.value = config.tokenConfigured;
		if (config.tokenConfigured) {
			await loadUsers();
		}
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load VectorSurv configuration.";
	} finally {
		loading.value = false;
	}
}

onMounted(load);
</script>

<template>
	<div class="container py-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div class="d-flex align-items-center">
				<RouterLink
					class="btn btn-outline-secondary me-3"
					to="/_/configuration/integration/vectorsurv"
					title="Back to VectorSurv"
				>
					<i class="bi bi-arrow-left"></i>
				</RouterLink>
				<div>
					<h1 class="mb-0">VectorSurv users</h1>
					<p class="text-muted mb-0">
						Which member files under which VectorSurv user
					</p>
				</div>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div v-if="loading" class="text-center p-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<div v-else class="card mb-4 integration-card">
			<div class="card-header">
				<div>
					<h2 class="h5 mb-0">Users</h2>
				</div>
			</div>
			<div class="card-body">
				<p class="text-muted">
					The organization's active users, and which of them still need a
					VectorSurv user to file under. Without one, a user's filings have no
					VectorSurv user to be attributed to.
				</p>

				<div
					v-if="!tokenConfigured"
					class="alert alert-warning mb-0"
					role="alert"
				>
					No token is stored, so there is no VectorSurv connection for a user to
					file under.
					<RouterLink to="/_/configuration/integration/vectorsurv/connection">
						Configure the connection
					</RouterLink>
					first.
				</div>

				<template v-else>
					<div v-if="usersError" class="alert alert-danger" role="alert">
						{{ usersError }}
					</div>

					<div v-if="usersLoading" class="text-muted">
						Loading organization users…
					</div>

					<div v-else-if="users.length === 0" class="text-muted">
						No active users in this organization.
					</div>

					<table v-else class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">User</th>
								<th scope="col">Username</th>
								<th scope="col">VectorSurv user</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="member in users" :key="member.id">
								<td>{{ member.displayName || member.username }}</td>
								<td class="text-muted">{{ member.username }}</td>
								<td>
									<span
										class="badge"
										:class="
											member.mapped ? 'text-bg-success' : 'text-bg-secondary'
										"
									>
										{{ member.mapped ? "mapped" : "not mapped" }}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</template>
			</div>
		</div>
	</div>
</template>

<style scoped>
.integration-card {
	border-left: 5px solid #0d6efd;
}
</style>
